import type { RedirectDto } from "@intelligems/ig-types/dtos/redirects";
import { formatDomain } from "@intelligems/ig-utils/common";

export const getFormattedCurrUrlString = (currentUrl: URL) => {
  return getDecodedUrl(
    formatDomainAndPathname(
      currentUrl.origin,
      currentUrl.pathname
    ).toLowerCase()
  );
};

export const isEncoded = (url: string) => {
  url = url || "";
  try {
    return decodeURIComponent(url) !== url;
  } catch {
    return false;
  }
};

export const getDecodedUrl = (url: string) => {
  while (isEncoded(url)) {
    url = decodeURIComponent(url);
  }
  return url;
};

// transforms partial destination/origin URL to full URL
export const ensureFullUrl = (
  redirect: RedirectDto,
  placeholderDomain?: string,
  key?: "destinationUrl" | "originUrl"
): string => {
  const defaultKey = key ?? "destinationUrl";
  const formattedPathname =
    redirect[defaultKey] && !redirect[defaultKey]?.startsWith("/") ? "/" : "";

  // if no period then is only pathname (old redirect structure)
  if (!redirect[defaultKey]?.includes(".")) {
    const domainOrigin = placeholderDomain ?? window.location.origin;
    return formatDomain(
      `${domainOrigin}${formattedPathname}${redirect[defaultKey]}`
    );
  } else {
    // is full url
    return formatDomain(`${redirect[defaultKey]}`);
  }
};

// adds original URL and IG query params to redirectTo URL
export const addUrlAttributes = (
  setCookie: (key: string, value: string) => string | undefined,
  redirect: RedirectDto,
  newUrl: URL,
  ogUrlHref?: string
) => {
  // add IG query params
  for (const queryParam of redirect.queryParams) {
    newUrl.searchParams.set(queryParam.key, queryParam.value);

    if (queryParam.key === "discount") {
      setCookie("discount_code", queryParam.value);
    }
  }

  // add og query params
  const ogUrl = new URL(ogUrlHref ?? window.location.href);
  for (const [queryParamKey, queryParamValue] of ogUrl.searchParams.entries()) {
    if (!newUrl.searchParams.has(queryParamKey) && queryParamKey !== "view") {
      newUrl.searchParams.set(queryParamKey, queryParamValue);
    }
  }
  // add any hash
  newUrl.hash = ogUrl.hash;

  return newUrl;
};

export const isMatchingPageRedirect = (
  redirect: RedirectDto,
  currentUrl: URL
): boolean => {
  // removes search params and hash
  const urlDomainPathOnly = getFormattedCurrUrlString(currentUrl);

  if (redirect.filter === "contains") {
    return urlDomainPathOnly.includes(redirect.originUrl);
  } else if (redirect.filter === "endsWith") {
    return urlDomainPathOnly.endsWith(redirect.originUrl);
  } else if (redirect.filter === "matchesRegex") {
    try {
      const originRegex = new RegExp(redirect.originUrl);
      return originRegex.test(urlDomainPathOnly);
    } catch (e) {
      return false;
    }
  } else {
    // filter is matchesExactly / none / undefined
    const originUrl = formatDomain(
      getDecodedUrl(
        ensureFullUrl(redirect, undefined, "originUrl").toLowerCase()
      )
    );

    // for headless local development
    if (currentUrl.hostname === "localhost") {
      const thisUrl = new URL(window.location.href);
      const origin = new URL(originUrl);
      origin.host = thisUrl.host;
      return urlDomainPathOnly.trim() === origin.href.trim();
    } else {
      return urlDomainPathOnly.trim() === originUrl.trim();
    }
  }
};

export const getRedirectedFrom = (redirect: RedirectDto, currentUrl: URL) => {
  if (redirect.filter === "matchesExactly" || redirect.filter === "none") {
    return getFormattedCurrUrlString(currentUrl);
  } else {
    return ensureFullUrl(redirect, undefined, "originUrl");
  }
};

export const getDestinationUrl = (
  redirect: RedirectDto,
  currentUrl: URL,
  placeholderDomain?: string,
  ogUrlHref?: string,
  key?: "originUrl" | "destinationUrl"
) => {
  const urlDomainPathOnly = getFormattedCurrUrlString(currentUrl);

  return new URL(
    redirect.redirectType === "partialReplacement" &&
      redirect.find &&
      redirect.destinationUrl
      ? urlDomainPathOnly.replaceAll(redirect.find, redirect.destinationUrl)
      : ensureFullUrl(redirect, placeholderDomain, key)
  );
};

export const getDestinationRedirectUrl = (
  setCookie: (key: string, value: string) => string | undefined,
  redirect: RedirectDto,
  currentUrl: URL,
  placeholderDomain?: string,
  ogUrlHref?: string,
  key?: "originUrl" | "destinationUrl"
) => {
  let newUrl = getDestinationUrl(
    redirect,
    currentUrl,
    placeholderDomain,
    ogUrlHref,
    key
  );
  newUrl = addUrlAttributes(setCookie, redirect, newUrl, ogUrlHref);
  return newUrl;
};

export const buildQualifyingUrlRedirectMap = (
  urlMap: Record<string, RedirectDto[]>,
  redirect: RedirectDto,
  currentUrl: URL
) => {
  const isRedirectValid = isMatchingPageRedirect(redirect, currentUrl);
  if (isRedirectValid) {
    urlMap[redirect.variationId] = [
      ...(urlMap[redirect.variationId] || []),
      redirect,
    ];
  }

  return urlMap;
};

export const isActionableRedirect = (redirect: RedirectDto) => {
  if (redirect.redirectType === "partialReplacement") {
    return (
      redirect &&
      redirect.destinationUrl !== null &&
      redirect.destinationUrl !== undefined &&
      !redirect.skip &&
      redirect.find
    );
  } else {
    return (
      redirect &&
      redirect.destinationUrl !== null &&
      redirect.destinationUrl !== undefined &&
      !redirect.skip
    );
  }
};

// removes leading and trailing / in pathname
export const formatPathname = (pathname: string) => {
  let str = pathname.startsWith("/") ? pathname.substring(1) : pathname;
  str = str.endsWith("/") ? str.substring(0, str.length - 1) : str;
  return str;
};

export const unFormatPathname = (pathname: string) => {
  return pathname.startsWith("/") ? pathname : "/" + pathname;
};

export const ensureHttps = (url: string) => {
  let modified = url.startsWith("https://")
    ? url
    : url.replace("http://", "https://");
  // if still no protocol, prepend https://
  if (!url.startsWith("https://")) {
    modified = "https://" + modified;
  }
  return modified;
};

export const formatDomainAndPathname = (domain: string, pathname: string) => {
  const connector = pathname === "/" ? "" : "/";
  return formatDomain(domain) + connector + formatPathname(pathname);
};

export function removeRedirectSession(url: URL) {
  let session: string | undefined = undefined;
  if (url.hash?.startsWith("#ig-redirect-session=")) {
    session = decodeURIComponent(url.hash.split("=")[1]);
    url.hash = "";
  } else if (url.searchParams.has("ig-redirect-session")) {
    session = url.searchParams.get("ig-redirect-session")?.trim() ?? "";
    url.searchParams.delete("ig-redirect-session");
  }
  return session?.split(",");
}

// favors using the hash as that won't be visible in referer
// and server logs, but if hash is already being used,
// fallback to query param
export function addRedirectSession(url: URL, session: string[]) {
  if (!url.hash) {
    url.hash = `ig-redirect-session=${encodeURIComponent(session.join(","))}`;
  } else {
    url.searchParams.set("ig-redirect-session", session.join(","));
  }
}
